import React, { useRef } from "react";
import ReactDOM from 'react-dom';
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "../Button";
import { showErrorToast, showSuccessToast } from "../../lib/ToastUtility";
import {
  QR_BRAND_COLOR,
  QR_BG_COLOR,
  DEFAULT_HIGH_RES_SIZE,
  downloadCanvasAsPng,
  getLogoDimensions,
} from "../../utils/qrCodeGenerator";

type WebQrModalProps = {
  open: boolean;
  title: string;
  webLink: string;
  kioskSecret: string;
  onClose: () => void;
};

function ensureQrEntryParam(link: string): string {
  const trimmed = link.trim();
  if (!trimmed) {
    return trimmed;
  }

  try {
    const url = new URL(trimmed);
    url.searchParams.set("entry", "qr");
    return url.toString();
  } catch {
    const hasQuery = trimmed.includes("?");
    const separator = hasQuery ? "&" : "?";
    const alreadyPresent = /[?&]entry=qr(?:&|$)/i.test(trimmed);
    return alreadyPresent ? trimmed : `${trimmed}${separator}entry=qr`;
  }
}

const WebQrModal: React.FC<WebQrModalProps> = ({
  open,
  title,
  webLink,
  kioskSecret,
  onClose,
}) => {
  const previewCanvasRef = useRef<HTMLDivElement>(null);
  const highResCanvasWrapperRef = useRef<HTMLDivElement>(null);

  const logoSrc = `${process.env.PUBLIC_URL || ""}/TYI_ICON_GREEN.png`;
  const qrWebLink = ensureQrEntryParam(webLink);

  const previewLogo = getLogoDimensions(64);
  const highResLogo = getLogoDimensions(1024);

  if (!open) {
    return null;
  }

  const getCleanFilename = () => {
    const cleanTitle = title
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return `${cleanTitle || "kiosk-web-link"}-qr.png`;
  };

  const handleCopy = async (value: string, type: "link" | "secret") => {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      showSuccessToast(type === "link" ? "Web link copied" : "Kiosk secret copied");
    } catch {
      showErrorToast("Could not copy. Please copy it manually.");
    }
  };

  const handleDownload = () => {
    const canvas = highResCanvasWrapperRef.current?.querySelector("canvas") as
      | HTMLCanvasElement
      | null;

    if (!canvas) {
      const fallbackCanvas = previewCanvasRef.current?.querySelector("canvas") as
        | HTMLCanvasElement
        | null;
      if (!fallbackCanvas) {
        showErrorToast("Could not generate QR image");
        return;
      }
      downloadCanvasAsPng(fallbackCanvas, getCleanFilename());
      showSuccessToast("QR downloaded");
      return;
    }

    downloadCanvasAsPng(canvas, getCleanFilename());
    showSuccessToast("QR downloaded");
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl border border-white/50 bg-white p-6 shadow-xl dark:border-white/10 dark:bg-slate-900"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h2>

        <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700/40 dark:bg-amber-900/20 dark:text-amber-200">
          Share only with trusted kiosk operators. This QR resolves to a private web payment link.
        </p>

        <div className="mt-5 grid gap-4 lg:grid-cols-[280px_1fr]">
          <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-4 dark:border-white/10">
            <div ref={previewCanvasRef} className="mx-auto w-fit">
              <QRCodeCanvas
                value={qrWebLink}
                size={240}
                marginSize={4}
                level="H"
                fgColor={QR_BRAND_COLOR}
                bgColor={QR_BG_COLOR}
                imageSettings={{
                  src: logoSrc,
                  height: previewLogo.height,
                  width: previewLogo.width,
                  excavate: true,
                }}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Kiosk Web Link
              </p>
              <textarea
                value={webLink}
                readOnly
                rows={4}
                className="w-full rounded-md border border-gray-300 bg-gray-50 p-3 font-mono text-xs text-gray-800 focus:outline-none dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
              />
            </div>

            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Kiosk Secret
              </p>
              <textarea
                value={kioskSecret}
                readOnly
                rows={2}
                className="w-full rounded-md border border-gray-300 bg-gray-50 p-3 font-mono text-xs text-gray-800 focus:outline-none dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
              />
            </div>
          </div>
        </div>

        {/* Hidden offscreen 4096px render container */}
        <div style={{ position: "absolute", left: -99999, top: -99999, visibility: "hidden" }}>
          <div ref={highResCanvasWrapperRef}>
            <QRCodeCanvas
              value={qrWebLink}
              size={DEFAULT_HIGH_RES_SIZE}
              marginSize={4}
              level="H"
              fgColor={QR_BRAND_COLOR}
              bgColor={QR_BG_COLOR}
              imageSettings={{
                src: logoSrc,
                height: highResLogo.height,
                width: highResLogo.width,
                excavate: true,
              }}
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button variant="tertiary" onClick={() => void handleCopy(kioskSecret, "secret")}>
            Copy Secret
          </Button>
          <Button variant="tertiary" onClick={() => void handleCopy(webLink, "link")}>
            Copy Link
          </Button>
          <Button variant="primary" onClick={handleDownload}>
            Download QR
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WebQrModal;
